import React, { useEffect, useState, useMemo } from 'react';
import { ArrowLeft, Shield, Check, X, Key } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { getRolesList } from '../../api/rbac';
import type { Role } from '../../types/rbac';

interface UserFormProps {
  initialData?: {
    id?: number;
    username: string;
    password?: string;
    name: string;
    phoneNo: string;
    email?: string;
    dob?: string;
    imei?: string;
    employeeId?: string;
    address?: string;
    designation?: string;
    role?: string;
    roles?: Array<{ id: number; name: string }>;
    roleIds?: number[];
    status?: 'Active' | 'Inactive' | 'ACTIVE' | 'INACTIVE';
    forRegion?: string;
    zone?: string;
  };
  isEdit?: boolean;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({
  initialData,
  isEdit = false,
  onSubmit,
  onCancel,
}) => {
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [roleSearchTerm, setRoleSearchTerm] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await getRolesList();
        if (res && res.data) {
          setAllRoles(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error('Error fetching roles list:', err);
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  const userSchema = z.object({
    username: z.string().min(1, 'Username is required'),
    name: z.string().min(1, 'Name is required'),
    password: isEdit
      ? z.string().transform((v) => v || '').refine((val) => !val || val.length >= 6, {
          message: 'Password must be at least 6 characters',
        })
      : z.string().min(6, 'Password must be at least 6 characters'),
    phoneNo: z.string().optional(),
    email: z.string().email('Invalid email address').optional().or(z.literal('')),
    dob: z.string().optional(),
    address: z.string().optional(),
    designation: z.string().optional(),
    status: z.enum(['Active', 'Inactive']).default('Active'),
    forRegion: z.string().optional(),
    zone: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      username: '',
      password: '',
      name: '',
      phoneNo: '',
      email: '',
      dob: '',
      address: '',
      designation: '',
      status: 'Active' as 'Active' | 'Inactive',
      forRegion: '',
      zone: '',
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        username: initialData.username || '',
        password: initialData.password || '',
        name: initialData.name || '',
        phoneNo: initialData.phoneNo || '',
        email: initialData.email || '',
        dob: initialData.dob || '',
        address: initialData.address || '',
        designation: initialData.designation || initialData.role || '',
        status: initialData.status === 'INACTIVE' || initialData.status === 'Inactive' ? 'Inactive' : 'Active',
        forRegion: initialData.forRegion || '',
        zone: initialData.zone || '',
      });

      if (Array.isArray(initialData.roleIds) && initialData.roleIds.length > 0) {
        setSelectedRoleIds(initialData.roleIds);
      } else if (Array.isArray(initialData.roles) && initialData.roles.length > 0) {
        setSelectedRoleIds(initialData.roles.map((r) => r.id));
      }
    }
  }, [initialData, reset]);

  // Sync roleIds matching initial role name if roleIds were not pre-provided
  useEffect(() => {
    if (initialData && allRoles.length > 0 && selectedRoleIds.length === 0) {
      if (initialData.role) {
        const match = allRoles.find(
          (r) => r.name.toLowerCase() === initialData.role?.toLowerCase()
        );
        if (match) {
          setSelectedRoleIds([match.id]);
        }
      }
    }
  }, [allRoles, initialData, selectedRoleIds.length]);

  const toggleRoleSelection = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const removeRole = (roleId: number) => {
    setSelectedRoleIds((prev) => prev.filter((id) => id !== roleId));
  };

  // Inherited permissions calculation across selected roles
  const inheritedPermissions = useMemo(() => {
    const permMap = new Map<string, string>();
    selectedRoleIds.forEach((roleId) => {
      const roleObj = allRoles.find((r) => r.id === roleId);
      if (roleObj && Array.isArray(roleObj.permissions)) {
        roleObj.permissions.forEach((p: any) => {
          const key = typeof p === 'object' ? p.permissionKey : p;
          const desc = typeof p === 'object' ? p.description : undefined;
          if (key && !permMap.has(key)) {
            permMap.set(key, desc || key);
          }
        });
      }
    });
    return Array.from(permMap.entries()).map(([key, desc]) => ({ key, desc }));
  }, [selectedRoleIds, allRoles]);

  const filteredRolesToSelect = useMemo(() => {
    if (!roleSearchTerm.trim()) return allRoles;
    const q = roleSearchTerm.toLowerCase();
    return allRoles.filter((r) => r.name.toLowerCase().includes(q));
  }, [allRoles, roleSearchTerm]);

  const onFormSubmit = (data: any) => {
    const selectedRoles = allRoles.filter((r) => selectedRoleIds.includes(r.id));
    const primaryRoleName = selectedRoles[0]?.name || data.designation || 'USER';

    onSubmit({
      ...data,
      roleIds: selectedRoleIds,
      role: primaryRoleName,
      roles: selectedRoles.map((r) => ({ id: r.id, name: r.name })),
    });
  };

  return (
    <div className="space-y-6 select-none max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onCancel}
          className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
          title="Go Back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
            {isEdit ? 'Edit User Details & Roles' : 'Create New Multi-Role User'}
          </h1>
          <p className="text-xs text-slate-500">
            Assign multiple enterprise roles and inspect inherited permission capabilities
          </p>
        </div>
      </div>

      {/* Form Container */}
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="space-y-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          {/* Left Column */}
          <div className="space-y-5">
            {/* Username */}
            <div>
              <label className="block text-slate-800 font-bold text-sm mb-2">
                User ID / Username <span className="text-rose-500">*</span>
              </label>
              {errors.username && (
                <span className="block text-rose-500 text-xs font-bold mb-1 text-end">
                  {errors.username.message}
                </span>
              )}
              <input
                type="text"
                placeholder="Enter Username"
                {...register('username')}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:border-[#C1272D] text-sm font-semibold text-slate-800"
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-slate-800 font-bold text-sm mb-2">
                Full Name <span className="text-rose-500">*</span>
              </label>
              {errors.name && (
                <span className="block text-rose-500 text-xs font-bold mb-1 text-end">
                  {errors.name.message}
                </span>
              )}
              <input
                type="text"
                placeholder="Enter Full Name"
                {...register('name')}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:border-[#C1272D] text-sm font-semibold text-slate-800"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-slate-800 font-bold text-sm mb-2">
                Email Address
              </label>
              {errors.email && (
                <span className="block text-rose-500 text-xs font-bold mb-1 text-end">
                  {errors.email.message}
                </span>
              )}
              <input
                type="email"
                placeholder="Enter Email"
                {...register('email')}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:border-[#C1272D] text-sm font-semibold text-slate-800"
              />
            </div>

            {/* Phone No */}
            <div>
              <label className="block text-slate-800 font-bold text-sm mb-2">
                Phone Number
              </label>
              <input
                type="text"
                placeholder="Enter Phone Number"
                {...register('phoneNo')}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:border-[#C1272D] text-sm font-semibold text-slate-800"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {/* Password */}
            <div>
              <label className="block text-slate-800 font-bold text-sm mb-2">
                Password {!isEdit && <span className="text-rose-500">*</span>}
              </label>
              {errors.password && (
                <span className="block text-rose-500 text-xs font-bold mb-1 text-end">
                  {errors.password.message}
                </span>
              )}
              <input
                type="password"
                placeholder={isEdit ? 'Leave blank to keep unchanged' : 'Enter Password'}
                {...register('password')}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:border-[#C1272D] text-sm font-semibold text-slate-800"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-slate-800 font-bold text-sm mb-2">
                Account Status
              </label>
              <select
                {...register('status')}
                className="w-full bg-slate-50 border border-slate-300 rounded-xl py-2.5 px-4 outline-none focus:border-[#C1272D] text-sm font-semibold text-slate-800"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Multi-Role Selection */}
            <div className="space-y-2">
              <label className="block text-slate-800 font-bold text-sm flex items-center justify-between">
                <span>Assign Roles (Multi-Select)</span>
                <span className="text-xs text-indigo-600 font-normal">
                  {selectedRoleIds.length} selected
                </span>
              </label>

              {/* Selected Role Tags */}
              <div className="min-h-[46px] p-2 rounded-xl bg-slate-50 border border-slate-300 flex flex-wrap gap-2 items-center">
                {selectedRoleIds.length === 0 ? (
                  <span className="text-xs text-slate-400 px-2 italic">
                    No roles selected yet. Click below to add.
                  </span>
                ) : (
                  selectedRoleIds.map((id) => {
                    const rObj = allRoles.find((r) => r.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold border border-indigo-200"
                      >
                        <Shield className="w-3 h-3 text-indigo-500" />
                        {rObj ? rObj.name : `Role #${id}`}
                        <button
                          type="button"
                          onClick={() => removeRole(id)}
                          className="hover:text-rose-600 p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })
                )}
              </div>

              {/* Role Picker Dropdown Trigger */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full py-2 px-3 rounded-lg border border-slate-300 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 text-left flex items-center justify-between"
                >
                  <span>{loadingRoles ? 'Loading Roles...' : '+ Add / Change Assigned Roles'}</span>
                  <span>{isDropdownOpen ? '▲' : '▼'}</span>
                </button>

                {isDropdownOpen && (
                  <div className="absolute z-30 mt-1 w-full bg-white border border-slate-300 rounded-xl shadow-xl p-3 space-y-2 max-h-60 overflow-y-auto">
                    <input
                      type="text"
                      placeholder="Search roles..."
                      value={roleSearchTerm}
                      onChange={(e) => setRoleSearchTerm(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg bg-slate-100 text-xs border border-slate-300 outline-none"
                    />

                    <div className="space-y-1 pt-1">
                      {filteredRolesToSelect.map((r) => {
                        const isChecked = selectedRoleIds.includes(r.id);
                        return (
                          <div
                            key={r.id}
                            onClick={() => toggleRoleSelection(r.id)}
                            className={`p-2 rounded-lg text-xs font-medium cursor-pointer flex items-center justify-between transition ${
                              isChecked
                                ? 'bg-indigo-50 text-indigo-700'
                                : 'hover:bg-slate-100'
                            }`}
                          >
                            <span className="font-bold">{r.name}</span>
                            {isChecked && <Check className="w-4 h-4 text-indigo-600" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Inherited Permissions Badge Preview Box */}
        {selectedRoleIds.length > 0 && (
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-700">
              <Key className="w-4 h-4 text-indigo-500" />
              <span>Inherited Permissions Preview ({inheritedPermissions.length} unique keys)</span>
            </div>
            {inheritedPermissions.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No permission keys attached to selected roles yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pt-1">
                {inheritedPermissions.map(({ key }) => (
                  <span
                    key={key}
                    className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-mono text-slate-700"
                  >
                    {key}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Form Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-100 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-8 py-2.5 rounded-xl bg-[#DCA10D] hover:bg-[#c28c00] text-white font-bold text-sm shadow-md transition"
          >
            {isEdit ? 'Update User & Roles' : 'Create User'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
